import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
  ShieldAlert, Landmark, Users,
  CheckCircle2, AlertCircle, Clock,
  ChevronRight, HelpCircle, Info
} from "lucide-react";

const emptyData = {
  supportedPartyYesNo: false,
  supportedPartyName: "",
  farmerOrgYesNo: false,
  farmerOrganisation: "",
  proKhalistanYesNo: false,
  proKhalistanSupport: "",
};

const CanadaInformationForm = () => {
  const [formData, setFormData] = useState(emptyData);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  useEffect(() => {
    fetchAPi();
  }, []);

  const fetchAPi = async () => {
    setLoading(true);
    try {
      const response = await makeRequestAuth("get", "/user/getPoliticalAffiliation");
      if (response.data) setPreview(response.data);
    } catch (err) {
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    let errorList = [];
    if (formData.supportedPartyYesNo && !formData.supportedPartyName.trim())
      errorList.push("Political Party Name is required");
    if (formData.farmerOrgYesNo && !formData.farmerOrganisation.trim())
      errorList.push("Farmer Organisation Name is required");
    if (formData.proKhalistanYesNo && !formData.proKhalistanSupport.trim())
      errorList.push("Pro Khalistan Organisation is required");
    return errorList;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo(0, 0);
      return;
    }
    setErrors([]);
    setLoading(true);
    try {
      await makeRequestAuth("post", "/user/createOrUpdatePoliticalAffiliation", formData);
      setPreview(formData);
      showSuccessToast("Information saved successfully ✅");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: <Clock size={14} /> },
    approve: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <CheckCircle2 size={14} /> },
    reject: { color: "bg-rose-50 text-rose-700 border-rose-200", icon: <AlertCircle size={14} /> },
  };

  return (
    <div className="max-w-4xl mx-auto py-4 antialiased font-normal">
      <ToastContainer />

      {/* Modern Loader */}
      {loading && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin"></div>
            <span className="font-normal text-[#132b45]">Processing Data...</span>
          </div>
        </div>
      )}

      {/* ERROR BOX */}
      {errors.length > 0 && (
        <div className="mb-6 bg-rose-50 border border-rose-200 rounded-2xl p-4 animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center gap-2 text-rose-700 mb-2 font-medium uppercase text-xs tracking-wider">
            <AlertCircle size={16} />
            <span>Missing Information</span>
          </div>
          <ul className="list-disc ml-5 space-y-1">
            {errors.map((err, i) => (
              <li key={i} className="text-rose-600 text-sm font-normal">{err}</li>
            ))}
          </ul>
        </div>
      )}

      {preview ? (
        /* ================= PREVIEW MODE ================= */
        <div className="space-y-6 animate-in zoom-in-95 duration-500">
          <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-8 py-5 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-xl shadow-sm"><ShieldAlert className="text-indigo-600" size={20} /></div>
                <h3 className="font-medium text-[#132b45] uppercase tracking-normal">Declaration Summary</h3>
              </div>
              <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full border shadow-sm font-normal text-[10px] uppercase tracking-widest ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.color || STATUS_CONFIG.pending.color}`}>
                {STATUS_CONFIG[preview?.status?.toLowerCase()]?.icon || STATUS_CONFIG.pending.icon}
                {preview?.status || "Pending"}
              </div>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-12">
                <PreviewCard label="Political Support" value={preview.supportedPartyYesNo ? "Yes" : "No"} subLabel="Party Name" subValue={preview.supportedPartyName} icon={<Landmark size={18} />} isActive={preview.supportedPartyYesNo} />
                <PreviewCard label="Farmer Organisation" value={preview.farmerOrgYesNo ? "Yes" : "No"} subLabel="Org Name" subValue={preview.farmerOrganisation} icon={<Users size={18} />} isActive={preview.farmerOrgYesNo} />
                <div className="md:col-span-2">
                  <PreviewCard label="Pro Khalistan Support" value={preview.proKhalistanYesNo ? "Yes" : "No"} subLabel="Organisation Detail" subValue={preview.proKhalistanSupport} icon={<ShieldAlert size={18} />} isActive={preview.proKhalistanYesNo} fullWidth />
                </div>
              </div>

              {preview.status === "reject" && (
                <button
                  onClick={() => { setPreview(null); setFormData(emptyData); }}
                  className="mt-10 group flex items-center justify-center gap-3 bg-[#132b45] hover:bg-black text-white w-full py-4 rounded-2xl font-normal transition-all active:scale-[0.98]"
                >
                  MODIFY DECLARATION <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ================= FORM MODE ================= */
        <form onSubmit={handleSubmit} className="space-y-6 animate-in slide-in-from-bottom-8 duration-700">
          <div className="bg-white rounded-[2.5rem] border border-slate-200 p-8 md:p-10 shadow-sm">

            <header className="mb-10 flex items-center gap-4 border-b border-slate-50 pb-6">
              <div className="h-14 w-14 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 shadow-inner">
                <Info size={30} />
              </div>
              <div>
                <h2 className="text-2xl font-medium text-[#132b45] tracking-tight">Affiliation Declaration</h2>
                <p className="text-slate-400 text-sm font-normal">Mandatory disclosure for international processing</p>
              </div>
            </header>

            <div className="space-y-10">
              <QuestionBlock
                icon={<Landmark size={20} />}
                label="Political Support"
                question="Do you support any political party?"
                yesValue={formData.supportedPartyYesNo}
                onRadioChange={(val) => handleChange("supportedPartyYesNo", val)}
              >
                {formData.supportedPartyYesNo && (
                  <Input label="Enter Full Party Name" value={formData.supportedPartyName} onChange={(v) => handleChange("supportedPartyName", v)} />
                )}
              </QuestionBlock>

              <QuestionBlock
                icon={<Users size={20} />}
                label="Farmers Association"
                question="Do you support any farmer organisation?"
                yesValue={formData.farmerOrgYesNo}
                onRadioChange={(val) => handleChange("farmerOrgYesNo", val)}
              >
                {formData.farmerOrgYesNo && (
                  <Input label="Enter Organisation Name" value={formData.farmerOrganisation} onChange={(v) => handleChange("farmerOrganisation", v)} />
                )}
              </QuestionBlock>

              <QuestionBlock
                icon={<ShieldAlert size={20} />}
                label="Security Declaration"
                question="Do you support any Pro Khalistan organisation?"
                yesValue={formData.proKhalistanYesNo}
                onRadioChange={(val) => handleChange("proKhalistanYesNo", val)}
                isWarning
              >
                {formData.proKhalistanYesNo && (
                  <Input label="Provide Details of Organisation" value={formData.proKhalistanSupport} onChange={(v) => handleChange("proKhalistanSupport", v)} />
                )}
              </QuestionBlock>
            </div>

            <div className="mt-12 flex flex-col items-center gap-4">
              <p className="text-[10px] text-slate-400 font-normal uppercase tracking-widest flex items-center gap-2">
                <HelpCircle size={12} /> Double check all fields before saving
              </p>
              <button type="submit" className="w-full bg-[#132b45] hover:bg-black text-white px-10 py-4 rounded-2xl font-normal shadow-xl shadow-blue-900/10 transition-all active:scale-[0.98]">
                SAVE & SUBMIT DECLARATION
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

/* --- REUSABLE COMPONENTS --- */

const QuestionBlock = ({ icon, label, question, yesValue, onRadioChange, children, isWarning }) => (
  <div className={`p-6 rounded-[2rem] border transition-all duration-300 ${yesValue ? (isWarning ? 'bg-rose-50/50 border-rose-100' : 'bg-indigo-50/30 border-indigo-100') : 'bg-white border-slate-100'}`}>
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-xl ${isWarning ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-600'}`}>{icon}</div>
        <div>
          <span className={`text-[10px] font-normal uppercase tracking-widest ${isWarning ? 'text-rose-400' : 'text-slate-400'}`}>{label}</span>
          <p className="text-sm font-medium text-[#132b45] leading-tight">{question}</p>
        </div>
      </div>
      <div className="flex bg-slate-100 p-1 rounded-xl w-fit">
        <button type="button" onClick={() => onRadioChange(true)} className={`px-6 py-1.5 rounded-lg text-xs font-normal transition-all ${yesValue === true ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>YES</button>
        <button type="button" onClick={() => onRadioChange(false)} className={`px-6 py-1.5 rounded-lg text-xs font-normal transition-all ${yesValue === false ? 'bg-white text-slate-600 shadow-sm' : 'text-slate-400'}`}>NO</button>
      </div>
    </div>
    <div className="animate-in fade-in slide-in-from-top-2 font-normal">{children}</div>
  </div>
);

const Input = ({ label, value, onChange }) => (
  <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
    <label className="text-[10px] font-normal text-slate-400 uppercase tracking-widest ml-1">{label}</label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Type here..."
      className="w-full bg-white border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-indigo-500 outline-none font-normal text-[#132b45] shadow-sm transition-all"
    />
  </div>
);

const PreviewCard = ({ icon, label, value, subLabel, subValue, isActive, fullWidth }) => (
  <div className={`flex gap-4 p-4 rounded-2xl border ${isActive ? 'bg-indigo-50/50 border-indigo-100 shadow-sm' : 'bg-white border-slate-100'}`}>
    <div className={`mt-1 h-10 w-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
      {icon}
    </div>
    <div className="flex-1">
      <span className="text-[10px] uppercase font-normal tracking-widest text-slate-400 block mb-1">{label}</span>
      <span className={`text-sm font-medium ${isActive ? 'text-indigo-700' : 'text-slate-700'}`}>{value}</span>
      {isActive && subValue && (
        <div className="mt-2 pt-2 border-t border-indigo-100">
          <span className="text-[9px] uppercase font-normal text-indigo-400 block">{subLabel}</span>
          <p className="text-xs font-normal text-indigo-900 leading-tight mt-0.5">{subValue}</p>
        </div>
      )}
    </div>
  </div>
);

export default CanadaInformationForm;